
import { computed, defineComponent, PropType } from 'vue';
import {
  Colour,
  getFeatureColourFromBackground,
  getForegroundColourFromBackground,
} from '../../../helpers/colour';
import TpxHeading from '../../atoms/type/heading.vue';
import TpxParagraph from '../../atoms/type/paragraph.vue';
import TpxList from '../../atoms/list/list.vue';
import TpxIcon from '../../atoms/icon/icon.vue';
import TpxImage from '../../atoms/media/image.vue';
import ArrowLink from '../../atoms/link/arrowLink.vue';
import './accordion.scss';

/**
 * TPX accordion
 *
 * @example
 * <code>import TpxAccordion from '@manifesto-tech/tpximpact-design-system/components/molecules/accordion/accordion.vue'</code>
 */
export default defineComponent({
  name: 'TpxAccordion',
  components: {
    TpxParagraph,
    TpxList,
    TpxHeading,
    TpxIcon,
    TpxImage,
    ArrowLink,
  },

  data() {
    return {
      expanded: false,
    };
  },

  props: {
    /**
     * Background colour of the main widget
     */
    backgroundColour: {
      default: Colour.White,
      type: String,
      validator: (value: Colour) => {
        return Object.values(Colour).includes(value);
      },
    },

    /**
     * Heading
     */
    heading: {
      type: String,
      required: true,
    },

    /**
     * Content of accordion, visible when openend
     */
    text: {
      type: String,
      required: true,
    },

    /**
     * Optional CTA link
     */
    link: {
      type: Object as PropType<typeof ArrowLink>,
    },
  },

  methods: {
    close() {
      this.expanded = false;
      const element = this.$refs['m-accordion__content'] as HTMLCanvasElement;
      element.style.maxHeight = '0';
      const cta = element.querySelector('.m-accordion__link');
      if (cta) {
        cta.ariaHidden = 'true';
        (cta as HTMLButtonElement).tabIndex = -1;
      }
    },
    toggle() {
      this.expanded = !this.expanded;
      const element = this.$refs['m-accordion__content'] as HTMLCanvasElement;


      if (this.expanded) {
        element.style.maxHeight = element.scrollHeight + 'px';
        const cta = element.querySelector('.m-accordion__link');
        if (cta) {
          cta.ariaHidden = 'false';
          (cta as HTMLButtonElement).tabIndex = 0;
        }


        const thisAccordionRow = this.$refs['accordionRow'] as HTMLElement;
        const thisAccordionRowIsInAHideAndSeekBlock = thisAccordionRow.closest(`[data-component="hideAndSeekBlock"]`) != null;

        if (thisAccordionRowIsInAHideAndSeekBlock) {
          // for the most recently opened accordion row in a hide and seek block, if it has media, replace the hide and seek block's media with this row's media
          const hideAndSeekBlockMediaContainer = document.querySelector("[data-component='hideAndSeekMedia']");
          const hideAndSeekBlockImgUrl = document.querySelector("[data-component='hideAndSeekBlockMedia']")?.querySelectorAll("p")[0].innerHTML;
          const hideAndSeekBlockImgName = document.querySelector("[data-component='hideAndSeekBlockMedia']")?.querySelectorAll("p")[1].innerHTML;
          const accordionRowMediaInfoElements = document.querySelectorAll("[data-component='hideAndSeekRowMedia']");
          const accordionRowMediaInfo = [];

          //first get the media for the correct row
          accordionRowMediaInfoElements.forEach((accordionRowMediaInfoElement) => {
            const headingMatches = this.$props.heading === accordionRowMediaInfoElement.querySelector("[data-component='hideAndSeekRowHeading']")?.innerHTML;
            const contentMatches = this.$props.text === accordionRowMediaInfoElement.querySelector("[data-component='hideAndSeekRowContent']")?.innerHTML;
            if (headingMatches && contentMatches) {
              const mediaName = accordionRowMediaInfoElement.querySelector("[data-component='hideAndSeekRowMediaName']")?.innerHTML;
              const mediaUrl = accordionRowMediaInfoElement.querySelector("[data-component='hideAndSeekRowMediaUrl']")?.innerHTML;
              accordionRowMediaInfo.push({ mediaUrl, mediaName });
            }
          })

          if (accordionRowMediaInfo.length == 1) {
            const mediaObj = accordionRowMediaInfo[0]
            const isVideo = mediaObj.mediaUrl.endsWith(".mp4") || mediaObj.mediaUrl.endsWith(".webm") || mediaObj.mediaUrl.endsWith(".ogg");
            const imgMarkup = `<img src="${mediaObj.mediaUrl}?width=600" alt="${mediaObj.mediaName}" class="a-image m-card__image o-media" loading="lazy" />`;
            const videoMarkup = `<video width="600" class="a-image m-card__image o-media" controls>
                    <source src="${mediaObj.mediaUrl}" type="video/mp4">
                    <source src="${mediaObj.mediaUrl}" type="video/webm">
                    <source src="${mediaObj.mediaUrl}" type="video/ogg">
                    Your browser does not support the video tag.
                </video>`
            const markupToSwapIn = isVideo ? videoMarkup : imgMarkup;
            hideAndSeekBlockMediaContainer.innerHTML = markupToSwapIn;
          }
          else {
            hideAndSeekBlockMediaContainer.innerHTML = `<img src="${hideAndSeekBlockImgUrl}?width=600" alt="${hideAndSeekBlockImgName}" class="a-image m-card__image o-media" loading="lazy">`;
          }
        }
      } else {
        element.style.maxHeight = '0';
        const cta = element.querySelector('.m-accordion__link');
        if (cta) {
          cta.ariaHidden = 'true';
          (cta as HTMLButtonElement).tabIndex = -1;
        }
      }
    },
  },

  setup(props) {
    return {
      colourClasses: computed(() => ({
        [`bg--${props.backgroundColour}`]: !!props.backgroundColour,
        [`text--${getForegroundColourFromBackground(props.backgroundColour)}`]:
          !!props.backgroundColour,
      })),
      featureColourClasses: computed(() => ({
        [`text--${getFeatureColourFromBackground(props.backgroundColour)}`]:
          !!props.backgroundColour,
      })),
    };
  },
});
